import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestsAPI, providerCompaniesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Plus, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
  SUBMITTED: 'bg-blue-100 text-blue-700 border-blue-200',
  REVIEW: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  COUNTERPROPOSAL_SENT: 'bg-purple-100 text-purple-700 border-purple-200',
  APPROVED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  CONFIRMED: 'bg-green-100 text-green-700 border-green-200',
};

const statusLabels = {
  DRAFT: 'Borrador',
  SUBMITTED: 'Enviada',
  REVIEW: 'En Revisión',
  COUNTERPROPOSAL_SENT: 'Contrapropuesta',
  APPROVED: 'Aprobada',
  CONFIRMED: 'Confirmada',
};

export default function RequestsListPage() {
  const navigate = useNavigate();
  const { isProvider, isAdmin, isExecutive } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [searchTerm, statusFilter, providerFilter, requests]);

  const loadData = async () => {
    try {
      const [requestsRes, providersRes] = await Promise.all([
        requestsAPI.getAll(),
        providerCompaniesAPI.getAll()
      ]);
      setRequests(requestsRes.data);
      setFilteredRequests(requestsRes.data);
      setProviders(providersRes.data);
    } catch (error) {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];

    if (searchTerm) {
      filtered = filtered.filter((r) =>
        r.requested_month.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.provider_company_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    if (providerFilter !== 'all') {
      filtered = filtered.filter((r) => r.provider_company_id === providerFilter);
    }

    setFilteredRequests(filtered);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="requests-list-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Solicitudes</h1>
          <p className="text-lg text-slate-600">
            {isProvider ? 'Gestiona tus solicitudes' : 'Todas las solicitudes del sistema'}
          </p>
        </div>
        {isProvider && (
          <Button onClick={() => navigate('/requests/create')} data-testid="create-request-button">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Solicitud
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Buscar por mes o proveedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="DRAFT">Borrador</SelectItem>
                <SelectItem value="SUBMITTED">Enviada</SelectItem>
                <SelectItem value="REVIEW">En Revisión</SelectItem>
                <SelectItem value="APPROVED">Aprobada</SelectItem>
              </SelectContent>
            </Select>
            {(isAdmin || isExecutive) && (
              <Select value={providerFilter} onValueChange={setProviderFilter}>
                <SelectTrigger data-testid="provider-filter">
                  <SelectValue placeholder="Filtrar por proveedor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los proveedores</SelectItem>
                  {providers.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12">
            <div className="text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-xl text-slate-500 mb-2">No se encontraron solicitudes</p>
              {isProvider && (
                <Button onClick={() => navigate('/requests/create')} variant="outline" className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Nueva Solicitud
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredRequests.map((request) => (
            <Card
              key={request.id}
              className="border-slate-200 hover:border-primary hover:shadow-md transition-all cursor-pointer"
              onClick={() => navigate(`/requests/${request.id}`)}
              data-testid={`request-card-${request.id}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-primary" />
                      Solicitud - {request.requested_month}
                    </CardTitle>
                    <CardDescription>
                      Creada el {new Date(request.created_at).toLocaleDateString('es-EC', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      {request.provider_company_name && (
                        <span className="ml-2 font-medium text-slate-700">• {request.provider_company_name}</span>
                      )}
                    </CardDescription>
                  </div>
                  <Badge className={statusColors[request.status]} data-testid={`status-badge-${request.id}`}>
                    {statusLabels[request.status]}
                  </Badge>
                </div>
              </CardHeader>
              {request.notes && (
                <CardContent>
                  <p className="text-sm text-slate-600">{request.notes}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}